import { ReactNode } from 'react';
import { RefereeFixture, RefereeOfficial } from '@lib/api/refereeAssignments';
import {
  RefereeAppointmentCardProps,
  RefereeName,
} from '@components/molecules/referee-appointment-card';
import { DEFAULT_LOCALE, Locale } from '@core/constants/locales.constants';

/** Resolves a crest node for a team by its API teamId (== cometTeam dataProviderId). */
export type CrestResolver = (
  teamId: string | number | null | undefined
) => ReactNode;

/**
 * Standard match officials shown on the card, in display order, with the API
 * role codes expanded to human labels. The first entry (Referee) becomes the
 * card's primary row; the remaining five render as the secondary grid.
 * Non-standard roles (Referee/AR/VAR Observer, Match Delegate, Minor Grade
 * Advisor, Referee Development Advisor, …) are dropped.
 *
 * `codes` is a list because the API emits more than one spelling for the same
 * role — Assistant VAR arrives as both "AVAR" and "Assistant VAR". Matching is
 * case- and whitespace-insensitive for the same reason.
 */
const STANDARD_ROLES: { codes: string[]; label: string }[] = [
  { codes: ['Referee'], label: 'Referee' },
  { codes: ['AR1'], label: 'Assistant Referee 1' },
  { codes: ['AR2'], label: 'Assistant Referee 2' },
  { codes: ['VAR'], label: 'VAR' },
  { codes: ['AVAR', 'Assistant VAR'], label: 'Assistant VAR' },
  { codes: ['4th Official'], label: '4th Official' },
];

const normaliseRole = (role?: string | null) =>
  role?.trim().toLowerCase() ?? '';

/** Map API officials → the card's ordered referee list (standard roles only). */
export const toRefereeNames = (
  officials: RefereeOfficial[] = []
): RefereeName[] =>
  STANDARD_ROLES.flatMap(({ codes, label }) => {
    const wanted = codes.map(normaliseRole);
    const match = officials.find((official) =>
      wanted.includes(normaliseRole(official.role))
    );
    return match ? [{ role: label, name: match.personName }] : [];
  });

/**
 * Format the round for display: numeric rounds get a "Round " prefix
 * ("13" → "Round 13"); named rounds pass through as-is ("Semi-Final").
 */
export const formatRound = (round?: string | null): string | undefined => {
  const trimmed = round?.trim();
  if (!trimmed) {
    return undefined;
  }
  return /^\d+$/.test(trimmed) ? `Round ${trimmed}` : trimmed;
};

/**
 * Map a referee-assignments API fixture to RefereeAppointmentCard props.
 * Crests are resolved via `resolveCrest` (cometTeam lookup with Site Settings
 * fallback); `matchDateUtc` may be null → the card shows "Date TBC".
 */
export const refereeFixtureToCardProps = (
  fixture: RefereeFixture,
  resolveCrest: CrestResolver,
  locale: Locale = DEFAULT_LOCALE
): RefereeAppointmentCardProps => ({
  homeTeam: {
    name: fixture.homeTeamName,
    crest: resolveCrest(fixture.homeTeamId),
  },
  awayTeam: {
    name: fixture.awayTeamName,
    crest: resolveCrest(fixture.awayTeamId),
  },
  competition: fixture.competitionName,
  round: formatRound(fixture.round),
  kickOffUtc: fixture.matchDateUtc,
  stadium: fixture.facilityName,
  referees: toRefereeNames(fixture.officials),
  locale,
  testId: `referee-appointment-card-${fixture.matchId}`,
});
